/**
 * Shared shapes for the report access indicators (PMGD-1785). The server
 * builds these from the database, and the admin screens run
 * `getReportAccessProblemsUtil` over them.
 */

/** route_params keys the hub sends to the society report endpoint (reports/[reportId]) */
export const societyReportRouteParamKeys = [
  "reportId",
  "workflowReportId",
  "societyReportId",
  "conferenceEventReportId",
] as const;

/** route_params keys the hub sends to the profile report endpoint (profiles/[profileId]/reports/[reportId]) */
export const profileReportRouteParamKeys = [
  "profileReportId",
  "profileConferenceEventReportId",
  "conferenceWorkflowInstanceProfileReportId",
  "reviewSessionReportProfileId",
  "reviewInstanceReportProfileId",
  "singleReviewInstanceReportProfileId",
  "fetchNewestWorkflowInstanceReportProfileId",
  "profileDashboardModuleId",
] as const;

/** Every route_params key that points at a report, matching AppRouteConfigCustomReportTableView */
export const reportRouteParamKeys = [
  ...societyReportRouteParamKeys,
  ...profileReportRouteParamKeys,
] as const;

/** Directories are fetched through the society directory endpoint, so they count as a society key */
export const directoryRouteParamKey = "directoryId";

export type ReportRouteParamKey =
  | (typeof reportRouteParamKeys)[number]
  | typeof directoryRouteParamKey;

export const isProfileReportRouteParamKey = (paramKey: string): boolean =>
  (profileReportRouteParamKeys as readonly string[]).includes(paramKey);

/** Some/every/none tag names, as on routes and reports */
export type ReportAccessTags = {
  /** The caller needs at least one of these tags */
  someTag: string[];
  /** The caller needs all of these tags */
  everyTag: string[];
  /** The caller must have none of these tags */
  noneTag: string[];
};

/** A report's access settings plus facts about what it returns */
export type ReportAccessReport = ReportAccessTags & {
  /** CustomReport primary key */
  customReportId: number;
  /** Report name shown to staff */
  reportName: string;
  /** CustomReport.base_table */
  baseTable: string;
  /** Anyone, including logged-out visitors, may run it */
  isPublic: boolean;
  /** Meant to run only for the signed-in profile */
  profileScoped: boolean;
  /** Hidden from the admin reports list */
  archived: boolean;
  /** Runs a raw SQL query instead of a Prisma select */
  hasQueryRaw: boolean;
  /** Select paths that return the whole additional_info / additional_information object */
  fullAdditionalInfoPaths: string[];
  /** Select paths that return the whole workflow instance payload */
  fullPayloadPaths: string[];
  /**
   * Run for the signed-in profile, the report's own rows are limited to that
   * profile (by the endpoint, or PROFILE_ID_PLACEHOLDER in the where clause)
   */
  profileScopeRowsFiltered: boolean;
  /** To-many relations on models with profile_id that the select doesn't limit to the signed-in profile */
  profileScopeUnfilteredRelationPaths: string[];
};

/** Which part of the app uses a report */
export enum ReportAccessConsumerType {
  appRoute = "appRoute",
  directory = "directory",
  dashboardModule = "dashboardModule",
  adminDashboardPage = "adminDashboardPage",
  conference = "conference",
  form = "form",
  defaultReport = "defaultReport",
}

/** A hub route that runs the report, directly or through a directory */
export type ReportAccessAppRouteConsumer = ReportAccessTags & {
  /** Discriminator */
  type: ReportAccessConsumerType.appRoute;
  /** Report the route uses */
  customReportId: number;
  /** AppRouteConfig primary key */
  appRouteConfigId: number;
  /** Route label */
  label: string;
  /** Route url */
  url: string;
  /** AppRouteConfig.is_public */
  isPublic: boolean;
  /** route_params key that points at the report, or directoryId */
  paramKey: string;
  /** Set when the route reaches the report through this directory */
  directoryId?: number;
};

/** Anything else that references a report */
export type ReportAccessOtherConsumer = {
  /** Discriminator */
  type: Exclude<ReportAccessConsumerType, ReportAccessConsumerType.appRoute>;
  /** Report it uses */
  customReportId: number;
  /** Primary key of the directory, module, page, conference, form or default report */
  id: number;
  /** Name shown to staff */
  label: string;
  /** Form question name, or which conference report it is */
  detail?: string;
};

export type ReportAccessConsumer =
  | ReportAccessAppRouteConsumer
  | ReportAccessOtherConsumer;

/** A route param that names a report that doesn't exist in the route's society */
export type ReportAccessMissingReport = {
  /** AppRouteConfig primary key */
  appRouteConfigId: number;
  /** Route label */
  label: string;
  /** Route url */
  url: string;
  /** route_params key */
  paramKey: string;
  /** The id the param holds */
  missingId: number;
};

export enum ReportAccessProblemSeverity {
  error = "error",
  warning = "warning",
  info = "info",
}

export enum ReportAccessProblemCode {
  ROUTE_PUBLIC_REPORT_NOT = "ROUTE_PUBLIC_REPORT_NOT",
  ROUTE_MEMBERS_REPORT_ADMIN_ONLY = "ROUTE_MEMBERS_REPORT_ADMIN_ONLY",
  REPORT_MORE_OPEN = "REPORT_MORE_OPEN",
  TAG_MISMATCH = "TAG_MISMATCH",
  PROFILE_KEY_NOT_PROFILE_SCOPED = "PROFILE_KEY_NOT_PROFILE_SCOPED",
  SOCIETY_KEY_PROFILE_SCOPED = "SOCIETY_KEY_PROFILE_SCOPED",
  FULL_ADDITIONAL_INFO = "FULL_ADDITIONAL_INFO",
  FULL_PAYLOAD = "FULL_PAYLOAD",
  PROFILE_SCOPED_ROWS_NOT_FILTERED = "PROFILE_SCOPED_ROWS_NOT_FILTERED",
  PROFILE_SCOPED_RELATION_NOT_FILTERED = "PROFILE_SCOPED_RELATION_NOT_FILTERED",
  QUERY_RAW_EXPOSED = "QUERY_RAW_EXPOSED",
  ARCHIVED_REFERENCED = "ARCHIVED_REFERENCED",
  FORM_REPORT_NOT_PUBLIC = "FORM_REPORT_NOT_PUBLIC",
  REPORT_NOT_FOUND = "REPORT_NOT_FOUND",
}

/** One misconfiguration, for one report and (when it applies) one consumer */
export type ReportAccessProblem = {
  /** What is wrong */
  code: ReportAccessProblemCode;
  /** error: breaks or leaks once PMGD-1763 enforces access; warning: review; info: FYI */
  severity: ReportAccessProblemSeverity;
  /** Report concerned (the missing id for REPORT_NOT_FOUND) */
  customReportId: number;
  /** Report name, when the report exists */
  reportName?: string;
  /** The route, directory, form or other consumer the problem is about */
  consumer?: ReportAccessConsumer;
  /** For REPORT_NOT_FOUND, the route param naming the missing report */
  missingReport?: ReportAccessMissingReport;
  /** Plain-language reason and fix, for staff */
  message: string;
};

/** Everything the indicators need for one society, or a slice of it */
export type ReportAccessSummary = {
  /** Reports in scope */
  reports: ReportAccessReport[];
  /** What uses each report */
  consumers: ReportAccessConsumer[];
  /** Route params naming reports that don't exist */
  missingReports: ReportAccessMissingReport[];
  /** Problems found across all of the above */
  problems: ReportAccessProblem[];
};
